/**
 * The pre-fill section's scope picker: the DISTRICT first, then each school.
 *
 * OVS is set by the district with school-level overrides (Sheila,
 * 2026-11-04), so the district is where the section opens and each school is
 * where its own numbers are overridden. This is `campus-scope.tsx`'s per-card
 * picker with the district added in front; every other card on the page keeps
 * the plain campus picker, because their settings exist only per campus.
 *
 * An admin of a single school gets no picker and no district entry — the
 * section renders exactly as the school card did before.
 */

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@ordo/ui/select'
import { type ReactNode, useMemo, useState } from 'react'

import type { AdminSchool } from '@/api/school-orders'

import { resolveScopedCampus } from './campus-scope'

const DISTRICT_PREFIX = 'district:'

export interface SplitDistrictScope {
  kind: 'district'
  organizationId: string
  organizationName: string
  schools: AdminSchool[]
}

export type SplitScopeSelection = SplitDistrictScope | { kind: 'school'; school: AdminSchool }

/** The districts this admin administers two or more schools in, by name. */
export function districtsOf(schools: readonly AdminSchool[]): SplitDistrictScope[] {
  const byId = new Map<string, SplitDistrictScope>()
  for (const school of schools) {
    const entry = byId.get(school.organizationId) ?? {
      kind: 'district' as const,
      organizationId: school.organizationId,
      organizationName: school.organization.name,
      schools: [],
    }
    entry.schools.push(school)
    byId.set(school.organizationId, entry)
  }
  return [...byId.values()]
    .filter((district) => district.schools.length > 1)
    .sort((a, b) => a.organizationName.localeCompare(b.organizationName))
}

/**
 * Resolve the picker value: a known district, else a known school, else the
 * first district, else the first school.
 */
export function resolveSplitScope(schools: AdminSchool[], selectedId: string | null): SplitScopeSelection | null {
  const districts = districtsOf(schools)
  if (selectedId?.startsWith(DISTRICT_PREFIX)) {
    const district = districts.find((d) => `${DISTRICT_PREFIX}${d.organizationId}` === selectedId)
    if (district) {
      return district
    }
  } else if (selectedId && schools.some((s) => s.id === selectedId)) {
    const school = resolveScopedCampus(schools, selectedId)
    return school ? { kind: 'school', school } : null
  }
  if (districts[0]) {
    return districts[0]
  }
  const school = resolveScopedCampus(schools, null)
  return school ? { kind: 'school', school } : null
}

export function useSplitScope(schools: AdminSchool[]): {
  selection: SplitScopeSelection | null
  scopeControl: ReactNode
  selectSchool: (locationId: string) => void
} {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selection = resolveSplitScope(schools, selectedId)
  const districts = useMemo(() => districtsOf(schools), [schools])
  const value = !selection
    ? ''
    : selection.kind === 'district'
      ? `${DISTRICT_PREFIX}${selection.organizationId}`
      : selection.school.id

  const scopeControl =
    selection && schools.length > 1 ? (
      <Select value={value} onValueChange={setSelectedId}>
        <SelectTrigger className="h-8 w-full max-w-64 text-xs sm:w-64" aria-label="District or school for pre-fill">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {districts.length > 0 ? (
            <SelectGroup>
              <SelectLabel>District default</SelectLabel>
              {districts.map((district) => (
                <SelectItem key={district.organizationId} value={`${DISTRICT_PREFIX}${district.organizationId}`}>
                  All schools — {district.organizationName}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null}
          <SelectGroup>
            <SelectLabel>School overrides</SelectLabel>
            {schools.map((school) => (
              <SelectItem key={school.id} value={school.id}>
                {school.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    ) : null

  return { selection, scopeControl, selectSchool: setSelectedId }
}
